import clsx from 'clsx';

/**
 * Bağımsız değişken (axis label vs) yok — sadece görsel trend göstergesi.
 * 7 günlük çağrı sayısı gibi küçük serileri özetler.
 *
 * - Smooth path (quadratic curves) yerine basit line — okunabilirlik için
 * - Son nokta için solid dot (bugün vurgusu)
 * - Min/max nokta hafif renkli vurgu
 * - SVG-only, dependency yok
 */
export function Sparkline({
  data,
  width = 140,
  height = 36,
  colorClass = 'text-brand-500',
  showLastDot = true,
}: {
  data: number[];
  width?: number;
  height?: number;
  colorClass?: string;
  showLastDot?: boolean;
}) {
  if (data.length === 0) {
    return <div className="text-[10px] text-slate-300" style={{ width, height }}>—</div>;
  }
  const padX = 2;
  const padY = 4;
  const max = Math.max(...data, 1);
  const min = Math.min(...data, 0);
  const range = Math.max(max - min, 1);

  const points = data.map((v, i) => {
    const x = padX + (i * (width - padX * 2)) / Math.max(data.length - 1, 1);
    const y = padY + ((max - v) / range) * (height - padY * 2);
    return { x, y, v };
  });

  const path = points.map((p, i) => (i === 0 ? `M${p.x},${p.y}` : `L${p.x},${p.y}`)).join(' ');
  // Area fill — sparkline'ın altı renkli (subtle gradient hissi)
  const areaPath = `${path} L${points[points.length - 1].x},${height - padY} L${points[0].x},${height - padY} Z`;
  const lastPoint = points[points.length - 1];

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
      <defs>
        <linearGradient id={`spark-grad-${colorClass}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="currentColor" stopOpacity="0.18" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
        </linearGradient>
      </defs>
      <g className={colorClass}>
        <path d={areaPath} fill={`url(#spark-grad-${colorClass})`} />
        <path d={path} fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" />
        {showLastDot && (
          <circle cx={lastPoint.x} cy={lastPoint.y} r={2.5} fill="currentColor" className="drop-shadow-sm" />
        )}
      </g>
    </svg>
  );
}

/** Custom mini bar chart — kullanıcı bar tercih ederse alternatif. */
export function Microbars({
  data,
  width = 140,
  height = 36,
  colorClass = 'text-brand-500',
}: {
  data: number[];
  width?: number;
  height?: number;
  colorClass?: string;
}) {
  if (data.length === 0) return null;
  const max = Math.max(...data, 1);
  const barWidth = (width - (data.length - 1) * 3) / data.length;

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className={clsx(colorClass)}>
      {data.map((v, i) => {
        const h = (v / max) * height;
        return (
          <rect
            key={i}
            x={i * (barWidth + 3)}
            y={height - h}
            width={barWidth}
            height={h}
            fill="currentColor"
            rx={1.5}
            opacity={i === data.length - 1 ? 1 : 0.5}
          />
        );
      })}
    </svg>
  );
}
